// src/pages/GridSetup.jsx
//
// Grid & Interconnection — everything about the point of interconnection in
// one place: connection level, import/export limits, and the transformer
// topology & loss modelling (dev spec section 4 / A3). The TransformerPanel
// used to live inside the PV and BESS setup pages; a standalone BESS never
// visited the PV page and hybrids buried the transformer inside "Equipment",
// so the interconnection now has a page of its own for every system type.
import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SetupPageHeader from "../components/SetupPageHeader";
import TransformerPanel from "../components/TransformerPanel";
import { useHistory } from "../context/HistoryContext";
import { DEFAULT_CONFIG } from "../components/config/defaultConfig";
import { useLanguage } from "../context/LanguageContext";
import { installedPvKw, percentageExportKw, reconcileGridExportLimit } from "../components/config/gridExportLimit";

// Same helper as in ConfigForm/BESSForm.
function setPathInObject(obj, path, value) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = { ...(cur[k] ?? {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

// Typical EU line-to-line voltage bands per connection level (mirrors the
// backend's CONNECTION_VOLTAGE_BANDS_V in read_config.py). The voltage is a
// real physical input: it drives the POI cable-run losses (∝ 1/V²) and the
// LV/MV/HV consistency checks.
const CONNECTION_VOLTAGE_BANDS = {
  LV: { min: 100, max: 1000, typical: 400, label: "100 V – 1 kV (typical 400 V)" },
  MV: { min: 1000, max: 36000, typical: 20000, label: "1 – 36 kV (Greece: 20 kV)" },
  HV: { min: 36000, max: 400000, typical: 150000, label: "36 – 400 kV (Greece: 150 kV)" },
};

export default function GridSetup() {
  const history = useHistory();
  const { isGreek } = useLanguage();
  const tr = (el, en) => isGreek ? el : en;
  const cfg = useMemo(() => history.present ?? DEFAULT_CONFIG, [history.present]);

  const grid = cfg.grid || {};
  const commit = (next) => history.commit(reconcileGridExportLimit(next));
  const handleGridField = (key, value) => commit(setPathInObject(cfg, `grid.${key}`, value));
  const percentMode = grid.exportLimitMode === "pv_percent";
  const [percentDraft, setPercentDraft] = useState(grid.exportLimitPercent ?? "");
  useEffect(() => setPercentDraft(grid.exportLimitPercent ?? ""), [grid.exportLimitPercent, percentMode]);
  const percentValid = Number.isFinite(Number(percentDraft)) && Number(percentDraft) > 0 && Number(percentDraft) <= 100;
  const pvKw = installedPvKw(cfg);
  const effectiveKw = percentageExportKw(cfg);
  const fmt = (value) => value?.toLocaleString(isGreek ? "el-GR" : "en-US", { maximumFractionDigits: 6 });
  const handleExportMode = (mode) => {
    const nextGrid = { ...grid, exportLimitMode: mode };
    if (mode === "pv_percent") {
      // Preserve an existing valid cap when changing its input representation.
      const ratio = Number(grid.exportLimitKw) / pvKw * 100;
      nextGrid.exportLimitPercent = Number.isFinite(ratio) && ratio > 0 && ratio <= 100
        ? Number(ratio.toFixed(6)) : 100;
    }
    commit({ ...cfg, grid: nextGrid });
  };

  const connectionLevel = grid.connectionLevel ?? "MV";
  const voltageBand = CONNECTION_VOLTAGE_BANDS[connectionLevel] || CONNECTION_VOLTAGE_BANDS.MV;
  const voltageV = grid.voltageV;
  const voltageOutOfBand =
    voltageV !== "" &&
    voltageV != null &&
    Number.isFinite(Number(voltageV)) &&
    (Number(voltageV) < voltageBand.min || Number(voltageV) > voltageBand.max);

  const handleConnectionLevel = (level) => {
    const band = CONNECTION_VOLTAGE_BANDS[level] || CONNECTION_VOLTAGE_BANDS.MV;
    let next = setPathInObject(cfg, "grid.connectionLevel", level);
    // Keep the voltage inside the new level's band: if the current value is
    // empty or belongs to the previous band, snap to the new typical value.
    const v = Number(next.grid?.voltageV);
    if (!Number.isFinite(v) || v <= 0 || v < band.min || v > band.max) {
      next = setPathInObject(next, "grid.voltageV", band.typical);
    }
    commit(next);
  };


  return (
    <Box sx={{ width: "100%", px: 0, mx: 0 }}>
      <Paper elevation={0} sx={{ p: 2 }}>
        <SetupPageHeader
          title="Grid Connection"
          description="The point of interconnection in one place: connection level, contracted import/export limits, and the transformer topology with its loss modelling. These settings apply across PV, wind and battery assets."
          sx={{ mb: 2 }}
        />

        {/* -------------------- GRID CONNECTION -------------------- */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                fontSize: "0.85rem",
                color: "text.primary",
              }}
            >
              Grid Connection
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  label="Connection Level"
                  value={connectionLevel}
                  onChange={(e) => handleConnectionLevel(e.target.value)}
                  helperText="Voltage level of the grid connection"
                >
                  <MenuItem value="LV">Low Voltage (LV)</MenuItem>
                  <MenuItem value="MV">Medium Voltage (MV)</MenuItem>
                  <MenuItem value="HV">High Voltage (HV)</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Connection Voltage (V, line-to-line)"
                  type="number"
                  value={voltageV ?? voltageBand.typical}
                  onChange={(e) =>
                    handleGridField(
                      "voltageV",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  inputProps={{ min: voltageBand.min, max: voltageBand.max, step: 10 }}
                  error={voltageOutOfBand}
                  helperText={
                    voltageOutOfBand
                      ? `Outside the typical ${connectionLevel} band: ${voltageBand.label} — check the units (V vs kV)`
                      : `${connectionLevel} band: ${voltageBand.label}. Drives the POI cable-run losses (∝ 1/V²).`
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} id="field-grid-export-limit">
                <TextField
                  fullWidth select sx={{ mb: 2 }}
                  label={tr("Καθορισμός ορίου έγχυσης", "Export limit input")}
                  value={percentMode ? "pv_percent" : "kw"}
                  onChange={(e) => handleExportMode(e.target.value)}
                  helperText={pvKw === null
                    ? tr("Για ορισμό ποσοστού, αποθηκεύστε πρώτα το ηλεκτρολογικό σχέδιο Φ/Β.", "Save the PV electrical design before setting a percentage.") : ""}
                >
                  <MenuItem value="kw">{tr("Σε kW", "In kW")}</MenuItem>
                  <MenuItem value="pv_percent" disabled={pvKw === null}>
                    {tr("Ως % της εγκατεστημένης ισχύος Φ/Β", "As % of installed PV capacity")}
                  </MenuItem>
                </TextField>
                {percentMode ? (
                  <>
                    <TextField fullWidth type="number"
                      label={tr("Ποσοστό εγκατεστημένης ισχύος Φ/Β (%)", "Installed PV capacity percentage (%)")}
                      value={percentDraft}
                      inputProps={{ min: 0.001, max: 100, step: "any" }}
                      error={!percentValid || effectiveKw === null}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setPercentDraft(raw);
                        const percent = Number(raw);
                        if (raw !== "" && Number.isFinite(percent) && percent > 0 && percent <= 100) handleGridField("exportLimitPercent", percent);
                      }}
                      onBlur={() => setPercentDraft(grid.exportLimitPercent ?? "")}
                      helperText={!percentValid || effectiveKw === null
                        ? tr("Απαιτούνται ηλεκτρολογικό σχέδιο και ποσοστό μεγαλύτερο από 0 έως 100%. Για μηδενική έγχυση επιλέξτε zero feed-in.", "An electrical design and a percentage above 0 and up to 100% are required. For no export, select zero feed-in.")
                        : tr("Το όριο ενημερώνεται όταν αλλάζει η εγκατεστημένη ισχύς Φ/Β.", "The limit updates when installed PV capacity changes.")}
                    />
                    {percentValid && effectiveKw !== null && (
                      <Typography variant="body2" sx={{ mt: 1 }} data-no-translate>
                        {tr("Εγκατεστημένη ισχύς Φ/Β", "Installed PV capacity")}: {fmt(pvKw)} kWp × {fmt(Number(grid.exportLimitPercent))}%
                        <br />
                        <b>{tr("Μέγιστη έγχυση στο δίκτυο", "Maximum grid export")}: {fmt(effectiveKw)} kW</b>
                      </Typography>
                    )}
                  </>
                ) : (
                <TextField
                  fullWidth
                  label="Export Limit (kW)"
                  type="number"
                  value={grid.exportLimitKw ?? ""}
                  onChange={(e) =>
                    handleGridField(
                      "exportLimitKw",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  helperText="Contracted injection cap at the POI. Leave empty for unlimited."
                />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  {tr("Αφορά τη συνολική έγχυση της εγκατάστασης στο δίκτυο, μαζί με την μπαταρία. Συμπληρώστε το όριο σύμφωνα με τους όρους σύνδεσης.", "Applies to total grid export from the installation, including the battery. Enter the limit specified in the connection terms.")}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Import Limit (kW)"
                  type="number"
                  value={grid.importLimitKw ?? ""}
                  onChange={(e) =>
                    handleGridField(
                      "importLimitKw",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  helperText="Contracted draw cap at the POI. Leave empty for unlimited."
                />
              </Grid>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(grid.enforceLimits)}
                      onChange={(e) => handleGridField("enforceLimits", e.target.checked)}
                    />
                  }
                  label="Enforce these limits as hard constraints in the dispatch optimisation (all revenue models)"
                />
              </Grid>
              <Grid size={12}>
                <Alert severity={grid.enforceLimits ? "info" : "warning"} variant="outlined">
                  {grid.enforceLimits
                    ? "The optimizer enforces the export/import caps as 24/7 hard bounds for every revenue model."
                    : "With enforcement OFF, these limits bind the dispatch only under Category 11A/11B and Behind-the-Meter. For plain merchant, tolling and PPA runs they are informational - the report will flag that the dispatch does not enforce them."}
                </Alert>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* -------------------- TRANSFORMERS & LOSS CHAIN -------------------- */}
        <TransformerPanel cfg={cfg} onCommit={commit} showTransformerErrors />
      </Paper>
    </Box>
  );
}
